import { useEffect } from 'react';
import Image from '../../components/image/Image';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import { useDispatch, useSelector } from 'react-redux';
import { addTaskCompletedMarking, fetchTaskCompletedMarkings } from '../tasks/tasks-actions';
import { getTaskCompletedMarkings } from '../tasks/tasks-reducer';
import {
    PARTICIPANTS_CAN_BE_INVITED_AFTER_PUBLISH_BANNER_OK,
    PARTICIPANTS_CAN_BE_INVITED_NOW_BANNER_OK,
} from '../tasks/tasks-model';
import { useProject } from '../project-hooks/useProject';
import { isActive, isProjectADugnad, isStartingInTheFutureNotPublished } from '../project-model';
import { Link, useParams } from 'react-router';
import { systemDateToHumanReadableDateWithOnlyDateFormat } from '../../utils';
import { ContentTitle } from '../../components/typography/Heading';
import styles from './subproject-invite-after-publish-reminder.module.less';

const SubprojectInviteAfterPublishReminder = ({ dismissible }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const project = useProject();
    const isDugnad = isProjectADugnad(project);
    const tasksCompleted = useSelector((state) => getTaskCompletedMarkings(state));
    const tasksLoading = useSelector((state) => state.tasks.isFetchingTaskCompletedMarkings);

    const canInviteParticipantsNow = isActive(project);
    const isStartingInTheFutureNotPublishedProject = isStartingInTheFutureNotPublished(project);

    const currentBannerTask = canInviteParticipantsNow
        ? PARTICIPANTS_CAN_BE_INVITED_NOW_BANNER_OK
        : PARTICIPANTS_CAN_BE_INVITED_AFTER_PUBLISH_BANNER_OK;

    useEffect(() => {
        SubprojectInviteAfterPublishReminder.fetchData(dispatch, params);
    }, [dispatch, params]);

    const addTaskCompleted = async () => {
        dispatch(addTaskCompletedMarking({ projectId: project.id, task: currentBannerTask }));
    };

    if (dismissible && (tasksLoading || tasksCompleted.includes(currentBannerTask))) {
        return null;
    }

    const projectTerminology = isDugnad ? 'dugnaden' : 'spleisen';

    const getContent = () => {
        if (canInviteParticipantsNow) {
            return (
                <div>
                    <span>Du kan nå invitere andre til å delta på {projectTerminology}. </span>
                    <Link
                        to={`/project/${project?.id}/control/subprojects`}
                        trackingCategory="project-tracking"
                        trackingAction="subproject-invite-after-publish"
                    >
                        Inviter deltakere
                    </Link>
                </div>
            );
        } else if (isStartingInTheFutureNotPublishedProject) {
            return (
                <span>
                    Når {projectTerminology} starter{' '}
                    <span className={styles.bold}>
                        {systemDateToHumanReadableDateWithOnlyDateFormat(project?.profile?.publishedAtOverride)}
                    </span>
                    , vil du få mulighet til å invitere andre til å delta på dugnaden.
                </span>
            );
        } else {
            return (
                <span>
                    Etter du har <span className={styles.bold}>startet</span> {projectTerminology}, vil du få mulighet
                    til å invitere deltakere!
                </span>
            );
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.grid}>
                <div className={styles.contentContainer}>
                    <div>
                        <ContentTitle>Inviter deltakere</ContentTitle>
                        {getContent()}
                        {dismissible && (
                            <div>
                                <RoundedButtonPrimary
                                    onClick={addTaskCompleted}
                                    className={styles.okButton}
                                    trackingCategory="project-tracking"
                                    trackingAction="subproject-invite-after-publish"
                                >
                                    Den er grei, skjul denne
                                </RoundedButtonPrimary>
                            </div>
                        )}
                    </div>
                    <Image
                        className={styles.image}
                        src={'product/dugnad-personal-page-illustration-with-hand.svg'}
                        alt={'Illustrasjon av en mobil med en perssonlig salgsside for dugnad'}
                    />
                </div>
            </div>
        </div>
    );
};

SubprojectInviteAfterPublishReminder.fetchData = (dispatch, params) => {
    dispatch(fetchTaskCompletedMarkings({ projectId: params.projectId }));
};

export default SubprojectInviteAfterPublishReminder;
